import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ImageViewerWithOverlay from '@/components/viewer/ImageViewerWithOverlay';
import VideoViewerWithOverlay from '@/components/viewer/VideoViewerWithOverlay';
import FindingsTab from '@/components/findings/FindingsTab';
import MetadataTab from '@/components/findings/MetadataTab';
import TechnicalLogsTab from '@/components/findings/TechnicalLogsTab';
import type { Finding, ModuleRun, ReportSummary, MediaType } from '@/types/forensics';

interface ResultsLayoutProps {
  findings: Finding[];
  moduleRuns: ModuleRun[];
  summary: ReportSummary | null;
  metadataSummary: Record<string, unknown>;
  imageWidth?: number;
  imageHeight?: number;
  previewUrl?: string | null;
  mediaType?: MediaType;
}

export default function ResultsLayout({
  findings,
  moduleRuns,
  summary,
  metadataSummary,
  imageWidth,
  imageHeight,
  previewUrl,
  mediaType = 'image',
}: ResultsLayoutProps) {
  const isVideo = mediaType === 'video';

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-3.5rem)] overflow-hidden">
      {/* Left pane - Media viewer */}
      <div className="lg:w-[65%] w-full h-[50vh] lg:h-full border-b lg:border-b-0 lg:border-r">
        {isVideo && previewUrl ? (
          <VideoViewerWithOverlay findings={findings} previewUrl={previewUrl} />
        ) : (
          <ImageViewerWithOverlay findings={findings} imageWidth={imageWidth} imageHeight={imageHeight} previewUrl={previewUrl} />
        )}
      </div>

      {/* Right pane - Findings & evidence */}
      <div className="lg:w-[35%] w-full flex-1 lg:h-full overflow-auto">
        <Tabs defaultValue="findings" className="h-full flex flex-col">
          <div className="border-b px-4 pt-2">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="findings">Findings</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
              <TabsTrigger value="technical">Technical</TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto p-4">
            <TabsContent value="findings" className="mt-0">
              <FindingsTab summary={summary} />
            </TabsContent>

            <TabsContent value="metadata" className="mt-0">
              <MetadataTab metadata={metadataSummary} />
            </TabsContent>

            <TabsContent value="technical" className="mt-0">
              <TechnicalLogsTab moduleRuns={moduleRuns} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
